import { useState, useEffect } from 'react';
import userService from '../services/userService';

/**
 * Хук для работы с текущим пользователем и его telegram_id
 */
export const useUser = () => {
  const [telegramId, setTelegramId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Получаем telegram_id из различных источников
    const getTelegramId = (): string | null => {
      // 1. Проверяем localStorage
      const storedId = localStorage.getItem('telegram_id');
      if (storedId) {
        console.log('[useUser] Telegram ID из localStorage:', storedId);
        return storedId;
      }

      // 2. Проверяем URL параметры (для случая, когда открывается через Telegram Web App)
      const urlParams = new URLSearchParams(window.location.search);
      const urlTelegramId = urlParams.get('telegram_id');
      if (urlTelegramId) {
        console.log('[useUser] Telegram ID из URL:', urlTelegramId);
        return urlTelegramId;
      }

      // 3. Проверяем Telegram WebApp API (если доступен)
      if (window.Telegram?.WebApp?.initDataUnsafe?.user?.id) {
        const telegramId = window.Telegram.WebApp.initDataUnsafe.user.id.toString();
        console.log('[useUser] Telegram ID из Telegram WebApp:', telegramId);
        return telegramId;
      }

      // 4. Для разработки - используем тестовый ID
      if (import.meta.env.DEV) {
        console.warn('Development mode: using test telegram_id');
        return 'test_user_123';
      }

      return null;
    };

    const id = getTelegramId();
    if (id) {
      // Очищаем от лишних символов (?, &, пробелы)
      const cleanId = id.trim().replace(/[?&]/g, '');
      setTelegramId(cleanId);
      // Сохраняем в localStorage для последующих сессий
      localStorage.setItem('telegram_id', cleanId);
      console.log('[useUser] Telegram ID установлен:', cleanId);
    }
    
    setIsLoading(false);
  }, []);

  return {
    telegramId,
    isLoading,
    setTelegramId: (id: string) => {
      setTelegramId(id);
      localStorage.setItem('telegram_id', id);
    }
  };
};

/**
 * Хук для работы с последним выбранным рестораном пользователя
 */
export const useUserRestaurant = (telegramId: string | null) => {
  const [lastSelectedRestaurantId, setLastSelectedRestaurantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLastRestaurant = async () => {
      if (!telegramId) {
        setIsLoading(false);
        return;
      }

      try {
        const restaurantId = await userService.getLastSelectedRestaurant(telegramId);
        setLastSelectedRestaurantId(restaurantId);
      } catch (error) {
        console.error('Error loading last selected restaurant:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLastRestaurant();
  }, [telegramId]);

  const updateLastRestaurant = async (restaurantId: string) => {
    if (!telegramId) {
      console.warn('Cannot update restaurant: telegram_id is not set');
      return false;
    }

    try {
      const success = await userService.updateLastSelectedRestaurant(telegramId, restaurantId);
      if (success) {
        setLastSelectedRestaurantId(restaurantId);
      }
      return success;
    } catch (error) {
      console.error('Error updating last selected restaurant:', error);
      return false;
    }
  };

  return {
    lastSelectedRestaurantId,
    isLoading,
    updateLastRestaurant
  };
};
